<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { collaborationApi } from '../services/collaboration';

const router = useRouter();
const notifications = ref<any[]>([]);
const preferences = ref<Record<string, boolean>>({});
const loading = ref(true);
const saving = ref(false);
const message = ref('');
const filter = ref<'all' | 'unread'>('all');
const page = ref(1);
const hasMore = ref(false);

function formatDate(value: number) { return new Date(value).toLocaleString(); }
function notificationText(item: any) {
  return item.message || item.body || item.title || `Actividad: ${String(item.type || 'notificación').replaceAll('_', ' ')}`;
}
async function load() {
  loading.value = true; message.value = '';
  try {
    page.value = 1;
    const [result, prefs] = await Promise.all([
      collaborationApi.notifications({ unread: filter.value === 'unread', limit: 25, page: page.value }),
      collaborationApi.notificationPreferences()
    ]);
    notifications.value = result.notifications || [];
    hasMore.value = notifications.value.length < Number(result.total || notifications.value.length);
    preferences.value = prefs.preferences || prefs;
  } catch (error: any) {
    message.value = error?.message || 'No se pudieron cargar las notificaciones.';
  } finally { loading.value = false; }
}
async function loadMore() {
  if (!hasMore.value || loading.value) return;
  loading.value = true;
  try {
    page.value += 1;
    const result = await collaborationApi.notifications({ unread: filter.value === 'unread', limit: 25, page: page.value });
    notifications.value.push(...(result.notifications || []));
    hasMore.value = notifications.value.length < Number(result.total || notifications.value.length);
  } catch (error: any) { message.value = error?.message || 'No se pudieron cargar más notificaciones.'; }
  finally { loading.value = false; }
}
async function markRead(item: any) {
  if (item.readAt || item.read) return;
  try {
    await collaborationApi.markNotificationRead(item.id);
    item.readAt = Date.now(); item.read = true;
  } catch (error: any) { message.value = error?.message || 'No se pudo marcar como leída.'; }
}
async function markAllRead() {
  try {
    await collaborationApi.markAllNotificationsRead();
    notifications.value.forEach(item => { item.read = true; item.readAt = item.readAt || Date.now(); });
  } catch (error: any) { message.value = error?.message || 'No se pudieron marcar como leídas.'; }
}
async function savePreferences() {
  saving.value = true;
  try { await collaborationApi.updateNotificationPreferences(preferences.value); message.value = 'Preferencias guardadas.'; }
  catch (error: any) { message.value = error?.message || 'No se pudieron guardar las preferencias.'; }
  finally { saving.value = false; }
}
async function openNotification(item: any) {
  await markRead(item);
  const caseId = item.caseId || item.data?.caseId;
  if (caseId) await router.push({ path: '/fase-40', query: { caseId } });
}
onMounted(load);
</script>

<template>
  <main class="notifications">
    <header class="notifications__header">
      <div><p class="eyebrow">FASE 41 · 496–502</p><h1>Notificaciones</h1><p>Invitaciones, cambios en casos, comentarios y menciones.</p></div>
      <div class="actions"><button type="button" @click="router.push('/')">← Tablero</button><button type="button" @click="markAllRead">Marcar todo como leído</button></div>
    </header>
    <p v-if="message" class="notice" role="status">{{ message }}</p>
    <section class="toolbar">
      <button type="button" :class="{active: filter === 'all'}" @click="filter = 'all'; load()">Todas</button>
      <button type="button" :class="{active: filter === 'unread'}" @click="filter = 'unread'; load()">No leídas</button>
    </section>
    <section class="layout">
      <section class="panel">
        <div v-if="loading" class="muted">Cargando notificaciones…</div>
        <div v-else-if="!notifications.length" class="muted">No tienes notificaciones pendientes.</div>
        <button v-for="item in notifications" :key="item.id" type="button" class="notification" :class="{unread: !(item.readAt || item.read)}" @click="openNotification(item)">
          <span class="notification__dot"></span><span><strong>{{ item.title || item.type || 'Notificación' }}</strong><span class="notification__text">{{ notificationText(item) }}</span><small>{{ formatDate(Number(item.createdAt || item.created_at || Date.now())) }}</small></span>
        </button>
        <button v-if="hasMore" type="button" class="loadMore" @click="loadMore">Cargar más</button>
      </section>
      <aside class="panel preferences">
        <h2>Preferencias</h2>
        <label v-for="(_, key) in preferences" :key="key"><input v-model="preferences[key]" type="checkbox"> {{ String(key).replaceAll('_', ' ') }}</label>
        <button type="button" :disabled="saving" @click="savePreferences">{{ saving ? 'Guardando…' : 'Guardar preferencias' }}</button>
      </aside>
    </section>
  </main>
</template>

<style scoped>
.notifications{min-height:100vh;box-sizing:border-box;padding:2rem clamp(1rem,4vw,4rem);background:#101827;color:#e5edf8;font-family:system-ui,sans-serif}.notifications__header{display:flex;justify-content:space-between;gap:1rem}.eyebrow{color:#6ee7b7;font-weight:700;letter-spacing:.08em}.notifications h1{margin:.2rem 0;font-size:clamp(1.8rem,4vw,3rem)}button{border:1px solid #334155;border-radius:.5rem;background:#172235;color:inherit;padding:.65rem;cursor:pointer;font-weight:700}.actions,.toolbar{display:flex;gap:.5rem;flex-wrap:wrap}.toolbar{margin:1.5rem 0}.toolbar .active{background:#25634d}.layout{display:grid;grid-template-columns:minmax(0,1.5fr) minmax(260px,.7fr);gap:1rem}.panel{background:#172235;border:1px solid #263852;border-radius:.8rem;padding:1rem}.notification{display:flex;width:100%;gap:.75rem;text-align:left;border:0;border-bottom:1px solid #2b3b54;border-radius:0}.notification.unread{background:#20324b}.notification__dot{width:.6rem;height:.6rem;margin-top:.35rem;border-radius:50%;background:#64748b;flex:none}.unread .notification__dot{background:#6ee7b7}.notification strong,.notification__text,.notification small{display:block}.notification__text{margin:.3rem 0;color:#c4d0df;font-weight:400}.notification small,.muted{color:#9fb0c7}.loadMore{display:block;width:100%;margin-top:.75rem}.preferences{display:grid;align-content:start;gap:.8rem}.preferences label{display:block}.notice{padding:.75rem;background:#493b18;border-radius:.5rem}@media(max-width:800px){.notifications__header,.layout{display:block}.actions{margin-top:1rem}.preferences{margin-top:1rem}}
</style>
